import type { Lottery } from '@lotta/shared/types-lottery-schemas';
import { DateTime } from 'luxon';

type RequiredLotteryProps = Pick<Lottery, 'occurrence' | 'drawDay' | 'drawTime'>;
type ReturnType = Array<{ startDate: string; endDate: string; drawDate: string }>;

/**
 * For a lottery, get the dates for all its draws (startDate, endDate, drawDate)
 */
export const getDrawDates = (lottery: RequiredLotteryProps, timezone: string): ReturnType => {
  const { startDate, endDate, occurrenceType } = lottery.occurrence;
  const drawHour = parseInt(lottery.drawTime.split(':')[0], 10);
  const start = DateTime.fromISO(startDate, { zone: timezone });
  const end = DateTime.fromISO(endDate, { zone: timezone }).endOf('day');

  switch (occurrenceType) {
    case 'MONTHLY':
      return getMonthlyDrawDates(start, end, lottery.drawDay, drawHour);
    case 'WEEKLY':
      return getWeeklyDrawDates(start, end, lottery.drawDay, drawHour);
    case 'SINGLE':
      return getSingleDrawDate(start, end, lottery.drawDay, drawHour);
    default: {
      // Causes typescript-error if not all cases are handled
      const exhaustiveCheck: never = occurrenceType;
      throw new Error(`Invalid occurrenceType '${exhaustiveCheck}'`);
    }
  }
};

const getMonthlyDrawDates = (
  lotteryStartDate: DateTime,
  lotteryEndDate: DateTime,
  drawDay: number,
  drawHour: number
): ReturnType => {
  const data: ReturnType = [];

  let nextDrawStartDate = lotteryStartDate;
  while (nextDrawStartDate < lotteryEndDate) {
    const startDate = nextDrawStartDate;
    const endDate = startDate.endOf('month');
    const drawDate = startDate.plus({ months: 1, days: drawDay - 1, hours: drawHour });
    data.push({
      startDate: startDate.toISO(),
      endDate: endDate.toISO(),
      drawDate: drawDate.toISO(),
    });
    nextDrawStartDate = nextDrawStartDate.plus({ months: 1 });
  }

  return data;
};

const getWeeklyDrawDates = (
  lotteryStartDate: DateTime,
  lotteryEndDate: DateTime,
  drawDay: number,
  drawHour: number
): ReturnType => {
  const data: ReturnType = [];

  const drawDelayDays = (drawDay - lotteryStartDate.weekday + 7) % 7;

  let nextDrawStartDate = lotteryStartDate;
  while (nextDrawStartDate < lotteryEndDate) {
    const startDate = nextDrawStartDate;
    const endDate = startDate.plus({ days: 6 }).endOf('day');
    const drawDate = startDate.plus({ days: 7 + drawDelayDays, hours: drawHour });
    data.push({
      startDate: startDate.toISO(),
      endDate: endDate.toISO(),
      drawDate: drawDate.toISO(),
    });
    nextDrawStartDate = nextDrawStartDate.plus({ days: 7 });
  }

  return data;
};

const getSingleDrawDate = (
  lotteryStartDate: DateTime,
  lotteryEndDate: DateTime,
  drawDay: number,
  drawHour: number
): ReturnType => {
  return [
    {
      startDate: lotteryStartDate.toISO(),
      endDate: lotteryEndDate.toISO(),
      drawDate: lotteryEndDate.startOf('day').plus({ days: drawDay, hours: drawHour }).toISO(),
    },
  ];
};
